import { useEffect, useState, type FormEvent } from 'react'
import { AlertCircle, ArrowRight, Eye, EyeOff } from 'lucide-react'
import { AuthShell } from './AuthShell.js'
import { login } from './api.js'
import { usePrefsStore, homeRoute, type Prefs } from '../prefs.js'
import { runLoginSuccess } from '../shell/auth-motion.js'
import { stashReturnToFromQuery, consumeReturnTo, wrongOrigin } from '../shell/auth-routing.js'
import { SecureOriginPage } from './InfoPages.js'

/** Après connexion : le `return_to` validé (LOT 6) prime ; sinon la
 *  préférence « page d'accueil » du compte décide de la destination. */
async function destinationApresConnexion(): Promise<string> {
  let home = '/'
  try {
    const r = await fetch('/api/v1/me/preferences', { credentials: 'same-origin' }).then((x) => x.json()) as { resolved: Prefs }
    home = homeRoute(r.resolved)
  } catch { /* accueil par défaut */ }
  return consumeReturnTo(home)
}

/**
 * /login — la porte d'entrée du portail.
 *
 * · Message d'erreur GÉNÉRIQUE (« Identifiants incorrects. ») : le formulaire
 *   ne révèle jamais si l'e-mail existe. Le détail vit dans l'audit interne.
 * · autocomplete username / current-password : les gestionnaires de mots de
 *   passe fonctionnent. Aucun CAPTCHA.
 */
export function LoginPage() {
  const { branding } = usePrefsStore()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [reveal, setReveal] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [remember, setRemember] = useState(false)
  const [rememberPolicy, setRememberPolicy] = useState<{ allowed: boolean; days: number }>({ allowed: false, days: 30 })
  const [mauvaiseOrigine, setMauvaiseOrigine] = useState<string | null>(null)

  useEffect(() => {
    // LOT 6 : le return_to arrive en query — validé puis mis de côté, il
    // survivra aux documents MFA jusqu'à la destination finale.
    stashReturnToFromQuery()
    fetch('/api/v1/auth/session', { credentials: 'same-origin' })
      .then((r) => r.json())
      .then((j: { remember_allowed?: boolean; remember_days?: number; public_origin?: string | null }) => {
        if (wrongOrigin(j.public_origin)) { setMauvaiseOrigine(j.public_origin ?? null); return }
        setRememberPolicy({ allowed: j.remember_allowed === true, days: j.remember_days ?? 30 })
      })
      .catch(() => undefined)
  }, [])

  if (mauvaiseOrigine !== null) return <SecureOriginPage origin={mauvaiseOrigine} />

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setError(null)
    try {
      const r = await login(email, password, remember)
      if (r.ok) {
        if (r.state === 'MFA_PENDING') window.location.assign('/login/mfa')
        else if (r.state === 'PASSWORD_CHANGE_REQUIRED') window.location.assign('/login/new-password')
        else if (r.state === 'MFA_ENROLLMENT_REQUIRED') window.location.assign('/login/mfa-setup')
        // Succès COMPLET : la signature LIVE CONTROL se joue, puis le shell.
        else await runLoginSuccess(await destinationApresConnexion())
        return
      }
      setError('Identifiants incorrects.')
    } catch {
      setError('Le serveur est injoignable. Réessayez dans un instant.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <AuthShell title={branding.app_name} subtitle={`Centre de contrôle — ${branding.org_name}`}>
      <form className="lc-form" onSubmit={submit} noValidate>
        {error ? (
          <p className="lc-form__error" role="alert">
            <AlertCircle size={16} aria-hidden="true" />
            {error}
          </p>
        ) : null}

        <div className="lc-field">
          <label className="lc-label" htmlFor="login-email">Adresse e-mail</label>
          <input
            id="login-email"
            className={`lc-input${error ? ' lc-input--error' : ''}`}
            type="email"
            name="username"
            autoComplete="username"
            spellCheck={false}
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={busy}
          />
        </div>

        <div className="lc-field">
          <label className="lc-label" htmlFor="login-password">Mot de passe</label>
          <div className="lc-field__wrap">
            <input
              id="login-password"
              className={`lc-input lc-input--haspad${error ? ' lc-input--error' : ''}`}
              type={reveal ? 'text' : 'password'}
              name="current-password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={busy}
            />
            <button
              type="button"
              className="lc-field__reveal"
              onClick={() => setReveal(!reveal)}
              aria-label={reveal ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
              aria-pressed={reveal}
            >
              {reveal ? <EyeOff size={17} /> : <Eye size={17} />}
            </button>
          </div>
        </div>

        {rememberPolicy.allowed ? (
          <label className="lc-auth__remember">
            <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} disabled={busy} />
            <span>Rester connecté pendant {rememberPolicy.days} jours</span>
          </label>
        ) : null}

        <button className="lc-auth__cta" type="submit" disabled={busy}>
          {busy ? <span className="lc-spin" aria-hidden="true" /> : null}
          {busy ? 'Connexion…' : 'Se connecter'}
          {!busy ? <ArrowRight size={17} aria-hidden="true" /> : null}
        </button>
      </form>
    </AuthShell>
  )
}
